import { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import apiClient from '../../api/apiClient'
import { Card } from '../ui/Card'
import {
  getRecommendationKrwMinor,
  getResourceKrwMinorAmounts,
  getTotalKrwMinor,
} from './cloudAdvisorSavings'
import type { Recommendation } from './cloudAdvisorSavings'

interface RecommendationResponse {
  currency: string
  totalMonthlyEstimatedSavings: number
  recommendations: Recommendation[]
}

interface CloudAdvisorRecommendationsProps {
  customerName: string
}

const krwFormatter = new Intl.NumberFormat('ko-KR', {
  style: 'currency',
  currency: 'KRW',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const percentFormatter = new Intl.NumberFormat('ko-KR', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const regionLabel: Record<string, string> = {
  'ap-seoul-1': 'South Korea Central (Seoul)',
  'ap-chuncheon-1': 'South Korea North (Chuncheon)',
  'us-ashburn-1': 'US East (Ashburn)',
  'us-phoenix-1': 'US West (Phoenix)',
  'us-chicago-1': 'US Midwest (Chicago)',
  'ap-tokyo-1': 'Japan East (Tokyo)',
  'ap-osaka-1': 'Japan Central (Osaka)',
  'ap-singapore-1': 'Singapore (Singapore)',
  'ap-sydney-1': 'Australia East (Sydney)',
  'eu-frankfurt-1': 'Germany Central (Frankfurt)',
  'uk-london-1': 'UK South (London)',
}

const importanceLabel: Record<string, string> = {
  CRITICAL: '매우 높음',
  HIGH: '높음',
  MODERATE: '보통',
  LOW: '낮음',
  MINOR: '매우 낮음',
}

const removeCostManagementPrefix = (value: string) => value.replace(/cost-management-/g, '')

const formatRecommendationName = (value: string) =>
  removeCostManagementPrefix(value).replace(/-name$/, '')

const formatKrwMinor = (krwMinor?: number | null) => {
  if (krwMinor === null || krwMinor === undefined || !Number.isFinite(krwMinor)) return '산정 중'
  return krwFormatter.format(krwMinor / 100)
}

const formatCount = (value?: number | null) =>
  value === null || value === undefined ? '-' : Number.isInteger(value) ? value.toFixed(0) : value.toString()

const formatOcpu = (count?: number | null, shape?: string | null) => {
  if (count === null || count === undefined) return '-'
  return `${formatCount(count)}${shape ? ` (${shape})` : ''}`
}

const formatRegion = (region?: string | null) => (region ? regionLabel[region] || region : '-')

const formatPercentage = (value?: number | null) =>
  value === null || value === undefined ? '-' : percentFormatter.format(value)

export function CloudAdvisorRecommendations({ customerName }: CloudAdvisorRecommendationsProps) {
  const [data, setData] = useState<RecommendationResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    let cancelled = false

    const loadRecommendations = async () => {
      setLoading(true)
      setError('')
      setData(null)

      try {
        const response = await apiClient.get<RecommendationResponse>(
          `/api/customers/${encodeURIComponent(customerName)}/cloud-advisor/recommendations`,
        )
        if (!cancelled) setData(response.data)
      } catch (err) {
        if (cancelled) return
        if (err instanceof AxiosError) {
          setError(err.response?.data?.message || '비용 절감 인사이트를 불러오지 못했습니다.')
        } else {
          setError('비용 절감 인사이트를 불러오지 못했습니다.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadRecommendations()
    return () => {
      cancelled = true
    }
  }, [customerName])

  return (
    <Card className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-700">
      <div className="p-4 md:p-6">
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="w-full flex items-center justify-between gap-4 text-left"
          aria-expanded={isOpen}
          aria-controls="cost-saving-insight-content"
        >
          <div className="flex min-w-0 items-center space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 dark:bg-emerald-700 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">비용 절감 인사이트</h2>
              <p className="mt-0.5 text-xs md:text-sm text-gray-600 dark:text-gray-400">
                월 예상 절감액이 있는 항목을 절감 효과가 큰 순서로 표시합니다.
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3 md:gap-5">
            {data && data.recommendations.length > 0 && (
              <div className="hidden text-right sm:block">
                <p className="text-xs text-gray-500 dark:text-gray-400">월 예상 절감 합계</p>
                <p className="text-lg md:text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatKrwMinor(getTotalKrwMinor(data.recommendations))}
                </p>
              </div>
            )}
            <svg
              className={`w-6 h-6 text-gray-700 dark:text-gray-300 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div id="cost-saving-insight-content" className="mt-5">
            {data && data.recommendations.length > 0 && (
              <div className="mb-4 rounded-xl bg-white/70 px-4 py-3 sm:hidden dark:bg-gray-900/40">
                <p className="text-xs text-gray-500 dark:text-gray-400">월 예상 절감 합계</p>
                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatKrwMinor(getTotalKrwMinor(data.recommendations))}
                </p>
              </div>
            )}

            {loading && (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-emerald-600 mx-auto mb-3" />
                비용 절감 인사이트를 불러오는 중...
              </div>
            )}

            {!loading && error && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-300">
                {error}
              </div>
            )}

            {!loading && !error && data?.recommendations.length === 0 && (
              <div className="rounded-xl bg-white/70 px-4 py-8 text-center text-sm text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                현재 월 예상 절감액이 있는 대기 중 항목이 없습니다.
              </div>
            )}

            {!loading && !error && data && data.recommendations.length > 0 && (
              <div className="space-y-4">
                {data.recommendations.map((recommendation, index) => {
                  const resources = recommendation.resources ?? []
                  const resourceKrwAmounts = getResourceKrwMinorAmounts(recommendation)
                  const showOcpuColumns = resources.some(
                    (resource) =>
                      resource.currentOcpuCount !== null && resource.currentOcpuCount !== undefined ||
                      resource.recommendedOcpuCount !== null && resource.recommendedOcpuCount !== undefined,
                  )
                  const showVersioningColumn = resources.some(
                    (resource) => resource.versioningEnabled !== null && resource.versioningEnabled !== undefined,
                  )
                  const showResourceStateColumn = resources.some((resource) => Boolean(resource.resourceState))
                  const showLevelColumn = resources.some((resource) => Boolean(resource.recommendationLevel))
                  const showAnalysisPeriodColumn = resources.some(
                    (resource) =>
                      resource.aggregationIntervalDays !== null && resource.aggregationIntervalDays !== undefined,
                  )

                  return (
                    <article
                      key={recommendation.id}
                      className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/50"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 dark:text-gray-500">#{index + 1}</span>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              중요도 {importanceLabel[recommendation.importance] || recommendation.importance}
                            </span>
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              대상 리소스 {resources.length}개
                            </span>
                          </div>
                          <h3 className="mt-2 break-words font-semibold text-gray-900 dark:text-gray-100">
                            {formatRecommendationName(recommendation.name)}
                          </h3>
                        </div>
                        <div className="shrink-0 sm:text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-400">월 예상 절감액</p>
                          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                            {formatKrwMinor(getRecommendationKrwMinor(recommendation))}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
                        <p className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">대상 리소스</p>
                        {resources.length === 0 ? (
                          <p className="rounded-lg bg-gray-50 px-3 py-4 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            OCI에서 제공한 대상 리소스 상세 정보가 없습니다.
                          </p>
                        ) : (
                          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="min-w-full divide-y divide-gray-200 text-left text-xs dark:divide-gray-700">
                              <thead className="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                <tr>
                                  <th className="whitespace-nowrap px-3 py-2.5 font-semibold">리소스명</th>
                                  <th className="whitespace-nowrap px-3 py-2.5 font-semibold">구획</th>
                                  <th className="whitespace-nowrap px-3 py-2.5 text-right font-semibold">월 예상 절감액</th>
                                  <th className="whitespace-nowrap px-3 py-2.5 text-right font-semibold">월 청구액 비중</th>
                                  <th className="whitespace-nowrap px-3 py-2.5 font-semibold">리전</th>
                                  {showOcpuColumns && (
                                    <>
                                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">현재 OCPU</th>
                                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">권장 OCPU</th>
                                    </>
                                  )}
                                  {showVersioningColumn && (
                                    <th className="whitespace-nowrap px-3 py-2.5 font-semibold">버전 관리</th>
                                  )}
                                  {showResourceStateColumn && (
                                    <th className="whitespace-nowrap px-3 py-2.5 font-semibold">리소스 상태</th>
                                  )}
                                  {showLevelColumn && (
                                    <th className="whitespace-nowrap px-3 py-2.5 font-semibold">권고 수준</th>
                                  )}
                                  {showAnalysisPeriodColumn && (
                                    <th className="whitespace-nowrap px-3 py-2.5 font-semibold">분석 기간</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-900/40">
                                {resources.map((resource, resourceIndex) => {
                                  const resourceName = resource.resourceName || resource.resourceId || '이름 없는 리소스'

                                  return (
                                    <tr key={resource.id || resource.resourceId} className="align-top">
                                      <td className="min-w-44 px-3 py-3 font-semibold text-gray-900 dark:text-gray-100">
                                        {resource.consoleUrl ? (
                                          <a
                                            href={resource.consoleUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="break-all text-blue-600 hover:underline dark:text-blue-400"
                                          >
                                            {resourceName}
                                          </a>
                                        ) : (
                                          <span className="break-all">{resourceName}</span>
                                        )}
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                        {resource.compartmentName || '-'}
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-3 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                                        {formatKrwMinor(resourceKrwAmounts[resourceIndex])}
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-3 text-right text-gray-600 dark:text-gray-300">
                                        {formatPercentage(resource.monthlyBillPercentage)}
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                        {formatRegion(resource.region)}
                                      </td>
                                      {showOcpuColumns && (
                                        <>
                                          <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                            {formatOcpu(resource.currentOcpuCount, resource.currentShape)}
                                          </td>
                                          <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                            {formatOcpu(resource.recommendedOcpuCount, resource.recommendedShape)}
                                          </td>
                                        </>
                                      )}
                                      {showVersioningColumn && (
                                        <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                          {resource.versioningEnabled === null || resource.versioningEnabled === undefined
                                            ? '-'
                                            : resource.versioningEnabled
                                              ? '사용'
                                              : '미사용'}
                                        </td>
                                      )}
                                      {showResourceStateColumn && (
                                        <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                          {resource.resourceState || '-'}
                                        </td>
                                      )}
                                      {showLevelColumn && (
                                        <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                          {resource.recommendationLevel || '-'}
                                        </td>
                                      )}
                                      {showAnalysisPeriodColumn && (
                                        <td className="whitespace-nowrap px-3 py-3 text-gray-600 dark:text-gray-300">
                                          {resource.aggregationIntervalDays === null || resource.aggregationIntervalDays === undefined
                                            ? '-'
                                            : `${formatCount(resource.aggregationIntervalDays)}일`}
                                        </td>
                                      )}
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </article>
                  )
                })}
              </div>
            )}

            <p className="mt-4 text-[11px] text-gray-400 dark:text-gray-500">
              OCI Cloud Advisor가 제공한 통화 코드를 사용합니다. KRW 금액은 환율을 다시 적용하지 않으며, USD인 경우에만
              1 USD = 1,445.3692 KRW를 적용합니다. 합계는 화면에 표시된 리소스별 원화 금액을 기준으로 계산합니다.
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}
